import { useMemo } from 'react'
import {
  useLoaderData,
  useRouteLoaderData,
  useParams,
  useNavigate,
  useSubmit,
} from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { styled } from '@mui/material/styles'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormHelperText from '@mui/material/FormHelperText'
import Checkbox from '@mui/material/Checkbox'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Rating from '@mui/material/Rating'
import Divider from '@mui/material/Divider'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'react-toastify'

import AbstractDetails from '../../components/AbstractDetails'
import { LoaderData } from '../../../../types'
import { createUniqueDeepClone } from '../../../../utils'
import {
  abstractDetailsForReviewLoader,
  abstractReviewListEventsLoader,
} from '../../Abstracts.loader'
import Heading from '../../../root/components/Heading'
import { AbstractReviewsSchema } from '../../Abstracts.schema'
import { defaultAbstractReview } from '../../Abstracts.utils'

const Form = styled('form')(() => ({
  display: 'flex',
  flexDirection: 'column',
  flex: '1 0',
  flexBasis: 'inherit',
}))

export default function AbstractReview() {
  const { t } = useTranslation('abstracts')
  const { eventId, abstractId } = useParams()
  const navigate = useNavigate()
  const submit = useSubmit()

  const { abstract, review } = useLoaderData() as LoaderData<
    ReturnType<typeof abstractDetailsForReviewLoader>
  >
  const events = useRouteLoaderData('review-abstracts-list') as LoaderData<
    ReturnType<typeof abstractReviewListEventsLoader>
  >

  const selectedEvent = useMemo(
    () => events?.find(event => event.id === Number(eventId)),
    [events, eventId]
  )

  const { getValues, handleSubmit, control, watch } = useForm<AbstractReviewsSchema>({
    defaultValues: review
      ? {
          ...review,
          has_conflict_of_interest: !!review.conflict_of_interest,
        }
      : createUniqueDeepClone(defaultAbstractReview()),
    resolver: zodResolver(AbstractReviewsSchema),
  })

  const onSubmitForm = (event: React.FormEvent<HTMLFormElement | HTMLButtonElement>) => {
    event.preventDefault()
    const values = getValues()
    const shouldUpdate = !!review

    try {
      const data = AbstractReviewsSchema.safeParse(values)

      if ('error' in data && !data.success) {
        toast.error(t("Le schéma de donnée n'est pas valide", { ns: 'common' }))
        data.error.issues.forEach(issue => {
          toast.warn(`${issue.path.toString()}: ${issue.message}`, {
            autoClose: 60000,
          })
        })
        console.error(data.error)
      }
      if ('data' in data && data.success) {
        submit(data.data, {
          method: 'POST',
          encType: 'application/json',
          action: `/abstracts/evaluation/${eventId}/rate/${abstractId}?shouldUpdate=${shouldUpdate}`,
        })
      }
    } catch (e) {
      console.error(e)
      toast.error(t("Le schéma de donnée n'est pas valide", { ns: 'common' }))
      toast.warn(t('Erreur innatendue', { ns: 'common' }))
    }
  }

  const hasConflictOfInterest = Boolean(watch('has_conflict_of_interest'))
  const score = watch('score')

  return (
    <Stack flex="1 1 auto" padding="1rem" gap="1rem">
      <Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap="1rem">
          <Typography variant="h2">{t('Évaluation du résumé')}</Typography>
          <Typography variant="h3">{selectedEvent?.name}</Typography>
        </Stack>
        <Divider />
      </Stack>

      <AbstractDetails abstract={abstract} />

      <Heading>
        <Typography variant="h3">{t('Comment évaluez-vous la qualité de ce résumé ?')}</Typography>
      </Heading>
      <Form onSubmit={onSubmitForm}>
        <Stack
          gap="1rem"
          width={'100%'}
          maxWidth={'60rem'}
          padding={'2rem'}
          margin="auto"
          sx={theme => ({
            backgroundColor: theme.palette.background.default,
            borderRadius: theme.shape.borderRadius,
          })}
        >
          <Controller
            control={control}
            name="has_conflict_of_interest"
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <>
                <FormControlLabel
                  name={field.name}
                  control={<Checkbox value={!!field.value} checked={!!field.value} />}
                  label={
                    <Typography variant="h4" className="MuiFormControlLabel-label">
                      {t("Je déclare un conflit d'intérêt avec ce résumé")}
                    </Typography>
                  }
                  onChange={field.onChange}
                  value={field.value}
                />
                {fieldState.error?.message && (
                  <FormHelperText error={!!fieldState.error}>
                    {fieldState.error?.message}
                  </FormHelperText>
                )}
              </>
            )}
          />

          <Controller
            control={control}
            name="conflict_of_interest"
            rules={{ required: hasConflictOfInterest }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label={t("Informations sur le conflit d'intérêt")}
                fullWidth
                multiline
                minRows={3}
                required={hasConflictOfInterest}
                error={fieldState.error?.message !== undefined}
                helperText={fieldState.error?.message}
                sx={{ display: hasConflictOfInterest ? undefined : 'none' }}
              />
            )}
          />

          <Stack sx={{ display: hasConflictOfInterest ? 'none' : undefined }} gap="1rem">
            <Typography variant="h4" className="MuiFormControlLabel-label">
              {t('Votre note pour ce résumé')} : {score}
            </Typography>
            <Controller
              control={control}
              name="score"
              rules={{ required: hasConflictOfInterest }}
              render={({ field, fieldState }) => (
                <>
                  <Rating
                    size="large"
                    max={10}
                    value={field.value}
                    onChange={e => {
                      const target = e.target as HTMLInputElement
                      const value = Number.isNaN(target.value) ? null : Number(target.value)

                      field.onChange(value)
                    }}
                  />
                  {fieldState.error?.message && (
                    <FormHelperText error={!!fieldState.error}>
                      {fieldState.error?.message}
                    </FormHelperText>
                  )}
                </>
              )}
            />

            <Controller
              control={control}
              name="proposed_topic_session"
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <>
                  <FormControlLabel
                    name={field.name}
                    control={
                      <Checkbox value={field.value} defaultChecked={field.value === 'true'} />
                    }
                    label={
                      <Typography variant="h4" className="MuiFormControlLabel-label">
                        {t(
                          'Je propose que ce résumé soit présenté en séance thématique lors des JFEF'
                        )}
                      </Typography>
                    }
                    onChange={field.onChange}
                    value={field.value}
                  />
                  {fieldState.error?.message && (
                    <FormHelperText error={!!fieldState.error}>
                      {fieldState.error?.message}
                    </FormHelperText>
                  )}
                </>
              )}
            />

            <Typography variant="h4" className="MuiFormControlLabel-label">
              {t('Commentaire')}
            </Typography>
            <Controller
              control={control}
              name="comment"
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  minRows={3}
                  error={fieldState.error?.message !== undefined}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Stack>
        </Stack>

        <Stack direction="row" justifyContent={'space-between'} gap="1rem">
          <Button
            variant="outlined"
            onClick={e => {
              e.preventDefault()
              e.stopPropagation()

              navigate(`/abstracts/evaluation/${eventId}`)
            }}
          >
            {t('Annuler')}
          </Button>
          <Button
            onClick={e => {
              void handleSubmit(
                () => {
                  onSubmitForm(e)
                },
                e => {
                  console.error(e)
                  toast.error(t('Veuillez corriger les erreurs'))
                }
              )()
            }}
            variant="contained"
          >
            {t('Valider')}
          </Button>
        </Stack>
      </Form>
    </Stack>
  )
}
